import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'

import { brandGroupIdAtom, surveyFilterAtom } from '~/atoms'
import { useCurrentPrimarySurvey } from '~/hooks/useCurrentPrimarySurvey'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useNotify } from '~/hooks/useNotify'

import {
  brandIdsAtom,
  importanceQuestionIdAtom,
  isTabulationFetchingAtom,
  isTabulationReadyAtom,
  lastTabulationParamsAtom,
  liftSaQuestionIdAtom,
  npiDriverQuestionIdAtom,
  whoAndWhatAwaitIdAtom,
} from '../atoms'

import { countWhoAndWhatMutation } from './useWhoAndWhatQueries'

export const useTriggerTabulation = () => {
  const survey = useCurrentPrimarySurvey()
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const brandIds = useAtomValue(brandIdsAtom)
  const npiDriverQuestionId = useAtomValue(npiDriverQuestionIdAtom)
  const importanceQuestionId = useAtomValue(importanceQuestionIdAtom)
  const liftSaQuestionId = useAtomValue(liftSaQuestionIdAtom)
  const setIsTabulationFetching = useSetAtom(isTabulationFetchingAtom)
  const setIsTabulationReady = useSetAtom(isTabulationReadyAtom)
  const setWhoAndWhatAwaitId = useSetAtom(whoAndWhatAwaitIdAtom)
  const setLastTabulationParams = useSetAtom(lastTabulationParamsAtom)
  const surveyFilter = useAtomValue(surveyFilterAtom)
  const graphQLRequest = useGraphQLRequest()
  const notify = useNotify()

  return React.useCallback(async () => {
    const surveyId = survey?.id
    const targetBrandId = brandIds[0]
    if (
      !targetBrandId ||
      !npiDriverQuestionId ||
      !importanceQuestionId ||
      !liftSaQuestionId ||
      !surveyId
    ) {
      return false
    }

    setWhoAndWhatAwaitId(null)
    setIsTabulationFetching(true)

    try {
      const result = await graphQLRequest({
        document: countWhoAndWhatMutation,
        variables: {
          surveyId,
          perceptionQuestionId: npiDriverQuestionId,
          keyPointQuestionId: importanceQuestionId,
          liftSaQuestionId,
          brandId: targetBrandId,
          customFilter: surveyFilter
            ? { filter: surveyFilter.filterGroups }
            : undefined,
        },
      })

      const awaitId = result.countWhoAndWhat?.awaitId
      if (awaitId) {
        setLastTabulationParams({
          brandGroupId,
          brandIds,
          npiDriverQuestionId,
          importanceQuestionId,
          liftSaQuestionId,
          surveyFilter: surveyFilter ?? null,
        })
        setWhoAndWhatAwaitId(awaitId)

        return true
      }

      setIsTabulationFetching(false)
      setIsTabulationReady(false)

      return false
    } catch {
      setIsTabulationFetching(false)
      setIsTabulationReady(false)
      setWhoAndWhatAwaitId(null)
      notify({
        content: '集計の開始に失敗しました。再度お試しください。',
        style: 'error',
      })

      return false
    }
  }, [
    survey,
    brandGroupId,
    brandIds,
    npiDriverQuestionId,
    importanceQuestionId,
    liftSaQuestionId,
    surveyFilter,
    setIsTabulationFetching,
    setIsTabulationReady,
    setWhoAndWhatAwaitId,
    setLastTabulationParams,
    graphQLRequest,
    notify,
  ])
}
